import React from 'react';
import { Box, useTheme } from '@mui/material';
import { getStringProduct } from '../../../../utils/utilityFunctions';

const SvgBackground = React.forwardRef(({ children, width = 400, height = 200, ...props }, ref) => {
  const theme = useTheme();
  // Generate unique IDs for SVG elements since we're using React 17
  const uniqueId = React.useMemo(() => Math.random().toString(36).substring(2, 11), []);

  return (
    <Box
      ref={ref}
      position="relative"
      width={width}
      height={height}
      sx={{
        [theme.breakpoints.up('xll')]: {
          width: getStringProduct(1.25, width),
          height: getStringProduct(1.25, height)
        },
        [theme.breakpoints.up('xlll')]: {
          width: getStringProduct(1.66, width),
          height: getStringProduct(1.66, height)
        },
        [theme.breakpoints.up('xl4')]: {
          width: getStringProduct(2.46, width),
          height: getStringProduct(2.46, height)
        },
      }}
      {...props}
    >
      {/* SVG Background */}
      <svg width="100%" height="100%" viewBox="0 0 400 200" fill="none" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" style={{ position: 'absolute', top: 0, left: 0 }}>
        <defs>
          <clipPath id={`clip0_${uniqueId}`}>
            <rect width="400" height="200" rx="8.75" fill="white" />
          </clipPath>

          <filter id={`filter0_f_${uniqueId}`} x="106.913" y="-55.5866" width="487.368" height="494.978" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="63.4183" result={`effect1_foregroundBlur_${uniqueId}`} />
          </filter>

          <filter id={`filter1_f_${uniqueId}`} x="-174.657" y="-242.055" width="487.368" height="494.978" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="63.4183" result={`effect2_foregroundBlur_${uniqueId}`} />
          </filter>

          <pattern id={`pattern0_${uniqueId}`} patternContentUnits="objectBoundingBox" width="15.3913" height="15.3913">
            <use xlinkHref={`#image0_${uniqueId}`} transform="scale(0.0150306)" />
          </pattern>

          <linearGradient id={`paint0_linear_${uniqueId}`} x1="6.95964" y1="7.60009e-06" x2="365.944" y2="200" gradientUnits="userSpaceOnUse">
            <stop stopColor="#00A894" />
            <stop offset="1" stopColor="#FF8A59" />
          </linearGradient>

          <image
            id={`image0_${uniqueId}`}
            width="1024"
            height="1024"
            preserveAspectRatio="none"
            xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAABAAAAAQACAYAAAB/HSuDAAAAAXNSR0IArs4c6QAAIABJREFUeF7snVFyHNfRrEzkULwH//7fHYd/4ivm0bOffcTKdf0yd/6J2+t3NPQYs9HN201uwgvXSJmUXOvUQPt87FAUBrdkx7mM7K6bnQh/X/aFydwaSXbnl23FwP3+rssIeXmrHVS61z9f+la/EZ9L8fr0HwfHCIpgAAAABJRU5ErkJggg=="
          />
        </defs>

        <g clipPath={`url(#clip0_${uniqueId})`}>
          <mask id={`path-2-inside-1_${uniqueId}`} fill="white">
            <path d="M230 0C230 6.90356 235.596 12.5 242.5 12.5C249.404 12.5 255 6.90356 255 0H391.25C396.082 5.15404e-06 400 3.91751 400 8.75V191.25C400 196.082 396.082 200 391.25 200H255C255 193.096 249.404 187.5 242.5 187.5C235.596 187.5 230 193.096 230 200H8.75C3.91751 200 0 196.082 0 191.25V8.75C0 3.91751 3.91751 0 8.75 0H230Z" />
          </mask>

          <path
            d="M230 0C230 6.90356 235.596 12.5 242.5 12.5C249.404 12.5 255 6.90356 255 0H391.25C396.082 5.15404e-06 400 3.91751 400 8.75V191.25C400 196.082 396.082 200 391.25 200H255C255 193.096 249.404 187.5 242.5 187.5C235.596 187.5 230 193.096 230 200H8.75C3.91751 200 0 196.082 0 191.25V8.75C0 3.91751 3.91751 0 8.75 0H230Z"
            fill="white"
          />

          <path
            d="M230 0H231.25V-1.25H230V0ZM255 0V-1.25H253.75V0H255ZM391.25 0V-1.25V0ZM255 200H253.75V201.25H255V200ZM230 200V201.25H231.25V200H230ZM8.75 200L8.75 201.25H8.75V200ZM230 0H228.75C228.75 7.59392 234.906 13.75 242.5 13.75V12.5V11.25C236.287 11.25 231.25 6.2132 231.25 0H230ZM242.5 12.5V13.75C250.094 13.75 256.25 7.59392 256.25 0H255H253.75C253.75 6.2132 248.713 11.25 242.5 11.25V12.5ZM255 0V1.25H391.25V0V-1.25H255V0ZM391.25 0V1.25C395.392 1.25 398.75 4.60787 398.75 8.75H400H401.25C401.25 3.22715 396.773 -1.24999 391.25 -1.25V0ZM400 8.75H398.75V191.25H400H401.25V8.75H400ZM400 191.25H398.75C398.75 195.392 395.392 198.75 391.25 198.75V200V201.25C396.773 201.25 401.25 196.773 401.25 191.25H400ZM391.25 200V198.75H255V200V201.25H391.25V200ZM255 200H256.25C256.25 192.406 250.094 186.25 242.5 186.25V187.5V188.75C248.713 188.75 253.75 193.787 253.75 200H255ZM242.5 187.5V186.25C234.906 186.25 228.75 192.406 228.75 200H230H231.25C231.25 193.787 236.287 188.75 242.5 188.75V187.5ZM230 200V198.75H8.75V200V201.25H230V200ZM8.75 200L8.75 198.75C4.60787 198.75 1.25 195.392 1.25 191.25H0H-1.25C-1.25 196.773 3.22716 201.25 8.75 201.25L8.75 200ZM0 191.25H1.25V8.75H0H-1.25V191.25H0ZM0 8.75H1.25C1.25 4.60786 4.60786 1.25 8.75 1.25V0V-1.25C3.22715 -1.25 -1.25 3.22715 -1.25 8.75H0ZM8.75 0V1.25H230V0V-1.25H8.75V0Z"
            fill={`url(#paint0_linear_${uniqueId})`}
            mask={`url(#path-2-inside-1_${uniqueId})`}
          />

          <g filter={`url(#filter0_f_${uniqueId})`}>
            <ellipse cx="350.598" cy="191.902" rx="116.848" ry="120.652" fill="#C111FF" fillOpacity="0.2" />
          </g>

          <g filter={`url(#filter1_f_${uniqueId})`}>
            <ellipse cx="69.0275" cy="5.43342" rx="116.848" ry="120.652" fill="#0043A8" fillOpacity="0.2" />
          </g>

          <circle cx="242.5" cy="200" r="12.5" fill="white" />

          <path d="M242.5 -12.5C249.404 -12.5 255 -6.90356 255 0C255 6.90356 249.404 12.5 242.5 12.5C235.596 12.5 230 6.90356 230 0C230 -6.90356 235.596 -12.5 242.5 -12.5Z" fill={`url(#pattern0_${uniqueId})`} fillOpacity="0.1" />

          <path d="M242.5 23.75V177.5" stroke="#FF8A58" strokeWidth="1.25" strokeMiterlimit="2.46052" strokeDasharray="7.34 11.01" />
        </g>
      </svg>

      {/* Children components that will be rendered on top of the SVG */}
      <Box position="relative" zIndex={1} width="100%" height="100%">
        {children}
      </Box>
    </Box>
  );
});

// Set display name for better debugging
SvgBackground.displayName = 'SvgBackground';

export default SvgBackground;
